import { faChevronLeft, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import * as React from "react";
import { useState } from "react";
import ReactPaginate from "react-paginate";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { Button, CardLink, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner,
} from "reactstrap";
import { Dispatch } from "redux";
import { ratingAction } from "../../actions/ratingAction";
import { DABDropDown } from "../../components/dab";
import { showAlert } from "../common/functions";

const PAGE_SIZE = 10;
const COLUMN_COUNT = 8;
const MAX_RATING = 5;
const SORT_COLUMN = "RatingDate";
const SORT_DIRECTION = "desc";

const DATE_RANGE_LAST_7_DAYS = "last7days";
const DATE_RANGE_CUSTOM = "custom";

const dateRangeOptions = [
	{ text: "Last 7 Days", value: DATE_RANGE_LAST_7_DAYS },
	{ text: "Custom", value: DATE_RANGE_CUSTOM },
];

// An empty value means "All" and is sent to the dashboard api as null
const platformOptions = [
	{ text: "All Platforms", value: "" },
	{ text: "iOS", value: "iOS" },
	{ text: "Android", value: "Android" },
];

const deviceTypeOptions = [
	{ text: "All Devices", value: "" },
	{ text: "Phone", value: "Phone" },
	{ text: "Tablet", value: "Tablet" },
];

const ratingOptions = [
	{ text: "All", value: "" },
	{ text: "1", value: "1" },
	{ text: "2", value: "2" },
	{ text: "3", value: "3" },
	{ text: "4", value: "4" },
	{ text: "5", value: "5" },
];

const DEFAULT_FILTERS = {
	dateRange: DATE_RANGE_LAST_7_DAYS,
	fromDate: "",
	toDate: "",
	platform: "",
	deviceType: "",
	appVersion: "",
	rating: "",
};

const getDateRange = (filters: any) => {
	if (filters.dateRange === DATE_RANGE_LAST_7_DAYS) {
		return {
			startDate: moment().subtract(6, "days").startOf("day").toISOString(),
			endDate: moment().endOf("day").toISOString(),
		};
	}
	return {
		startDate: moment(filters.fromDate, "YYYY-MM-DD").startOf("day").toISOString(),
		endDate: moment(filters.toDate, "YYYY-MM-DD").endOf("day").toISOString(),
	};
};

const buildRequestBody = (filters: any, pageNumber: number) => {
	const { startDate, endDate } = getDateRange(filters);

	return {
		sortDirection: SORT_DIRECTION,
		sortColumn: SORT_COLUMN,
		pageNumber: pageNumber,
		pageSize: PAGE_SIZE,
		startDate: startDate,
		endDate: endDate,
		platform: filters.platform || null,
		deviceType: filters.deviceType || null,
		appVersion: filters.appVersion || null,
		rating: filters.rating ? Number(filters.rating) : null,
	};
};

const validateFilters = (filters: any) => {
	if (filters.dateRange !== DATE_RANGE_CUSTOM) {
		return "";
	}
	if (!filters.fromDate || !filters.toDate) {
		return "Both From and To dates are required.";
	}
	if (new Date(filters.fromDate) > new Date(filters.toDate)) {
		return "'From' date cannot be later than 'To' date.";
	}
	return "";
};

const formatNumber = (value: any) => (+(value ?? 0)).toLocaleString("en-US");

interface RatingListProps {
	dispatch: Dispatch<any>;
	ratingData: any;
}

const RatingList: React.SFC<RatingListProps> = ({ dispatch, ratingData }) => {
	const history = useHistory();

	// filters holds the dropdown values, appliedFilters is what the list was loaded with
	const [filters, setFilters] = useState<any>(DEFAULT_FILTERS);
	const [appliedFilters, setAppliedFilters] = useState<any>(DEFAULT_FILTERS);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [dateError, setDateError] = useState<string>("");
	const [showLoader, setShowLoader] = useState(false);

	const [selectedRatingId, setSelectedRatingId] = useState<number>(-1);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const dashboardData = ratingData?.dashboardData;
	const summary = dashboardData?.summary;
	const ratings = dashboardData?.ratings ?? [];
	const totalCount = +(dashboardData?.totalCount ?? 0);
	const appVersions = ratingData?.appVersions ?? [];

	const appVersionOptions = [
		{ text: "All Versions", value: "" },
		...appVersions.map((version: any) => ({ text: version, value: version })),
	];

	const loadRatings = (filterValues: any, pageNumber: number) => {
		setShowLoader(true);
		dispatch(ratingAction.ratingGetDashboard(buildRequestBody(filterValues, pageNumber)));
	};

	const handleError = (status: any, defaultMessage: string) => {
		if (status === "Unauthorized") {
			sessionStorage.setItem("fromPath", window.location.pathname + window.location.search);
			history.push("/401");
		} else {
			showAlert(typeof status === "string" && status ? status : defaultMessage, true);
		}
	};

	React.useEffect(() => {
		dispatch(ratingAction.ratingGetAppVersions());
		loadRatings(DEFAULT_FILTERS, 1);
	}, []);

	React.useEffect(() => {
		if (showLoader && ratingData?.isListComplete) {
			setShowLoader(false);
			if (ratingData.isListError) {
				handleError(ratingData.status, "Unable to load the ratings.");
			}
		}
	}, [ratingData?.isListComplete]);

	React.useEffect(() => {
		if (ratingData?.isAppVersionError) {
			handleError(ratingData.status, "Unable to load the app versions.");
		}
	}, [ratingData?.isAppVersionError]);

	React.useEffect(() => {
		if (isDeleting && ratingData?.isDeleteComplete) {
			setIsDeleting(false);
			setShowDeleteModal(false);
			setSelectedRatingId(-1);

			if (ratingData.isDeleteError) {
				handleError(ratingData.status, "Unable to delete the rating.");
				return;
			}

			showAlert(ratingData.status || "Rating deleted successfully.", false);
			const nextPage =
				ratings.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
			setCurrentPage(nextPage);
			loadRatings(appliedFilters, nextPage);
		}
	}, [ratingData?.isDeleteComplete]);

	const handleFilterChange = (key: string, value: any) => {
		setFilters({ ...filters, [key]: value });
		setDateError("");
	};

	const handleApply = () => {
		const error = validateFilters(filters);
		if (error) {
			setDateError(error);
			return;
		}
		setDateError("");
		setAppliedFilters(filters);
		setCurrentPage(1);
		loadRatings(filters, 1);
	};

	const handleReset = () => {
		setFilters(DEFAULT_FILTERS);
		setAppliedFilters(DEFAULT_FILTERS);
		setDateError("");
		setCurrentPage(1);
		loadRatings(DEFAULT_FILTERS, 1);
	};

	const handleSelectForDelete = (userRatingId: number) => {
		setSelectedRatingId(userRatingId);
		setShowDeleteModal(true);
	};

	const handleCancelDelete = () => {
		setShowDeleteModal(false);
		setSelectedRatingId(-1);
	};

	const handleConfirmDelete = () => {
		setIsDeleting(true);
		dispatch(ratingAction.ratingDelete(selectedRatingId));
	};

	const renderStars = (rating: any) => {
		const value = Math.max(0, Math.min(MAX_RATING, Math.round(+rating || 0)));
		return (
			<span
				className="rating-stars"
				title={`${value} out of ${MAX_RATING}`}
				data-testid="ratingStars"
			>
				{"★".repeat(value)}
				{"☆".repeat(MAX_RATING - value)}
			</span>
		);
	};

	const renderSummaryCard = (
		testId: string,
		title: string,
		value: any,
		caption: string
	) => (
		<Col md="6" lg="3" className="mb-3 mb-lg-0">
			<div className="rating-summary-card" data-testid={testId}>
				<div className="rating-summary-title">{title}</div>
				<div className="rating-summary-value" data-testid={`${testId}Value`}>
					{value}
				</div>
				<div className="rating-summary-caption">{caption}</div>
			</div>
		</Col>
	);

	const renderRatingRow = (data: any) => (
		<tr key={data?.userRatingId}>
			<td id="userName" className="rating-username">
				{data?.userName || "-"}
			</td>
			<td id="rating" className="text-center">
				{renderStars(data?.rating)}
			</td>
			<td id="ratingDate">
				{data?.ratingDate ? moment(data.ratingDate).format("MM/DD/YYYY") : "-"}
			</td>
			<td id="deviceType">{data?.deviceType || "-"}</td>
			<td id="platform">{data?.platform || "-"}</td>
			<td id="appVersion">{data?.appVersion || "-"}</td>
			<td id="feedback" className="rating-feedback">
				{data?.feedback?.trim() ? data.feedback : "-"}
			</td>
			<td className="text-center">
				<FontAwesomeIcon
					icon={faTrashAlt}
					className="rating-delete-icon"
					data-testid="ratingDeleteButton"
					cursor="pointer"
					title="Delete"
					onClick={() => handleSelectForDelete(data?.userRatingId)}
				/>
			</td>
		</tr>
	);

	const firstItem = totalCount > 0 ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
	const lastItem = totalCount > 0 ? firstItem + ratings.length - 1 : 0;

	return (
		<div className="rating-list mb-5" data-testid="ratingListPage">
			<div className="card-box-link-back-div header-back mb-2">
				<FontAwesomeIcon icon={faChevronLeft} />
				<CardLink
					onClick={() => history.push("/")}
					className="card-box-link-back"
					data-testid="rating-backtolist"
				>
					Back to List
				</CardLink>
			</div>
			<p className="text-muted small" data-testid="ratingListDescription">
				This dashboard contains ratings and feedback captured inside the DAB
				application, not public store reviews.
			</p>

			<div className="rating-section" data-testid="ratingSummarySection">
				<div className="rating-section-title" data-testid="ratingSummaryTitle">
					Filtered Rating Summary
				</div>
				<div className="rating-section-body">
					<Row className="align-items-end" data-testid="ratingFilters">
						<Col md="4" lg className="mb-3">
							<Label htmlFor="dateRangeFilter" className="rating-filter-label">
								Date Range
							</Label>
							<DABDropDown
								id="dateRangeFilter"
								options={dateRangeOptions}
								value={filters.dateRange}
								onChange={(e) => handleFilterChange("dateRange", e.target.value)}
							/>
						</Col>
						<Col md="4" lg className="mb-3">
							<Label htmlFor="platformFilter" className="rating-filter-label">
								Platform
							</Label>
							<DABDropDown
								id="platformFilter"
								options={platformOptions}
								value={filters.platform}
								onChange={(e) => handleFilterChange("platform", e.target.value)}
							/>
						</Col>
						<Col md="4" lg className="mb-3">
							<Label htmlFor="deviceTypeFilter" className="rating-filter-label">
								Device Type
							</Label>
							<DABDropDown
								id="deviceTypeFilter"
								options={deviceTypeOptions}
								value={filters.deviceType}
								onChange={(e) => handleFilterChange("deviceType", e.target.value)}
							/>
						</Col>
						<Col md="4" lg className="mb-3">
							<Label htmlFor="appVersionFilter" className="rating-filter-label">
								App Version
							</Label>
							<DABDropDown
								id="appVersionFilter"
								options={appVersionOptions}
								value={filters.appVersion}
								onChange={(e) => handleFilterChange("appVersion", e.target.value)}
							/>
						</Col>
						<Col md="4" lg className="mb-3">
							<Label htmlFor="ratingFilter" className="rating-filter-label">
								Rating
							</Label>
							<DABDropDown
								id="ratingFilter"
								options={ratingOptions}
								value={filters.rating}
								onChange={(e) => handleFilterChange("rating", e.target.value)}
							/>
						</Col>
						<Col md="4" lg className="mb-3 d-flex gap-2">
							<Button
								type="button"
								color="primary"
								className="flex-fill"
								data-testid="applyFilterButton"
								onClick={handleApply}
							>
								Apply
							</Button>
							<Button
								type="button"
								color="light"
								className="flex-fill rating-reset-button"
								data-testid="resetFilterButton"
								onClick={handleReset}
							>
								Reset
							</Button>
						</Col>
					</Row>
					{filters.dateRange === DATE_RANGE_CUSTOM && (
						<Row className="mb-3" data-testid="customDateRange">
							<Col md="4" lg="3">
								<Label htmlFor="fromDateFilter" className="rating-filter-label">
									From <span className="text-danger">*</span>
								</Label>
								<Input
									type="date"
									id="fromDateFilter"
									data-testid="fromDateFilter"
									value={filters.fromDate}
									max={filters.toDate || undefined}
									onChange={(e) => handleFilterChange("fromDate", e.target.value)}
								/>
							</Col>
							<Col md="4" lg="3">
								<Label htmlFor="toDateFilter" className="rating-filter-label">
									To <span className="text-danger">*</span>
								</Label>
								<Input
									type="date"
									id="toDateFilter"
									data-testid="toDateFilter"
									value={filters.toDate}
									min={filters.fromDate || undefined}
									onChange={(e) => handleFilterChange("toDate", e.target.value)}
								/>
							</Col>
							{dateError && (
								<Col xs="12">
									<span className="text-danger small" data-testid="dateFilterError">
										{dateError}
									</span>
								</Col>
							)}
						</Row>
					)}
					<Row data-testid="ratingSummary">
						{renderSummaryCard(
							"averageRating",
							"Average Internal Rating",
							`${(+(summary?.averageRating ?? 0)).toFixed(1)} / ${MAX_RATING}`,
							"Based on each user's latest/current rating only."
						)}
						{renderSummaryCard(
							"ratingClicks",
							"Rating Clicks",
							formatNumber(summary?.ratingClicksUniqueUsers),
							"Unique Users"
						)}
						{renderSummaryCard(
							"remindMeLaterTotalClicks",
							"Remind Me Later",
							formatNumber(summary?.remindMeLaterTotalClicks),
							"Total Clicks"
						)}
						{renderSummaryCard(
							"remindMeLaterUniqueUsers",
							"Remind Me Later",
							formatNumber(summary?.remindMeLaterUniqueUsers),
							"Unique Users"
						)}
					</Row>
				</div>
			</div>

			<div className="rating-section" data-testid="ratingTableSection">
				<div className="rating-section-title" data-testid="ratingTableTitle">
					Ratings
				</div>
				<div className="rating-section-body">
					<div className="rating-table table-responsive">
						<table className="table table-bordered mb-0" data-testid="ratingTable">
							<thead data-testid="ratingTableHead">
								<tr>
									<th scope="col" data-testid="username-th">User Name</th>
									<th scope="col" className="text-center" data-testid="rating-th">Rating</th>
									<th scope="col" data-testid="ratingdate-th">Rating Date</th>
									<th scope="col" data-testid="devicetype-th">Device Type</th>
									<th scope="col" data-testid="platform-th">Platform</th>
									<th scope="col" data-testid="appversion-th">App Version</th>
									<th scope="col" data-testid="feedback-th">Feedback</th>
									<th scope="col" className="text-center" data-testid="actions-th">Actions</th>
								</tr>
							</thead>
							<tbody data-testid="ratingTableBody">
								{showLoader ? (
									<tr>
										<td colSpan={COLUMN_COUNT} className="text-center">
											<Spinner />
										</td>
									</tr>
								) : ratings.length > 0 ? (
									ratings.map((data: any) => renderRatingRow(data))
								) : (
									<tr>
										<td colSpan={COLUMN_COUNT} className="text-center h6">
											No Data found.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
					{!showLoader && totalCount > 0 && (
						<div className="d-flex flex-wrap justify-content-between align-items-center mt-3 gap-2">
							<span className="text-muted small" data-testid="ratingPaginationInfo">
								Showing {firstItem}–{lastItem} of {formatNumber(totalCount)} ratings
							</span>
							<ReactPaginate
								previousLabel={"Previous"}
								nextLabel={"Next"}
								breakLabel={"..."}
								pageCount={Math.ceil(totalCount / PAGE_SIZE)}
								marginPagesDisplayed={2}
								pageRangeDisplayed={3}
								onPageChange={(data) => {
									setCurrentPage(data.selected + 1);
									loadRatings(appliedFilters, data.selected + 1);
								}}
								containerClassName={"pagination rating-pagination mb-0"}
								pageClassName={"page-item"}
								previousClassName={"page-item"}
								breakClassName={"page-item"}
								breakLinkClassName={"page-link"}
								nextClassName={"page-item"}
								pageLinkClassName={"page-link"}
								previousLinkClassName={"page-link"}
								nextLinkClassName={"page-link"}
								activeClassName={"active"}
								forcePage={currentPage - 1}
							/>
						</div>
					)}
				</div>
			</div>

			<Modal
				isOpen={showDeleteModal}
				backdrop="static"
				centered
				className="rating-delete-modal"
				data-testid="ratingDeleteModal"
			>
				<ModalHeader>Delete Rating?</ModalHeader>
				<ModalBody className="modal-body">
					<div data-testid="ratingDeleteMessage">
						Are you sure you want to delete this rating? This rating will no
						longer be included in the Average Rating.
					</div>
				</ModalBody>
				<ModalFooter>
					<Button
						type="button"
						color="light"
						className="rating-reset-button"
						disabled={isDeleting}
						onClick={handleCancelDelete}
						data-testid="cancelDeleteButton"
					>
						Cancel
					</Button>
					<Button
						type="button"
						color="danger"
						disabled={isDeleting}
						onClick={handleConfirmDelete}
						data-testid="confirmDeleteButton"
					>
						{isDeleting && <Spinner size="sm"></Spinner>} Delete Rating
					</Button>
				</ModalFooter>
			</Modal>
		</div>
	);
};

const mapStateToProps = (state: any) => {
	const { ratingData } = state;
	return {
		ratingData,
	};
};

export default connect(mapStateToProps)(RatingList);
